import React from 'react';
import { InputAdornment, styled, TextField, Typography } from '@mui/material';
import FormField from 'components/form-page/FormField';
import MoneyInput from 'components/masked-input/MoneyInput';
import { palette } from 'components/record-shell/RecordShell';
import { marginFromSalePrice } from 'helpers/margin';
import { moneyFormat, percentFormat } from 'helpers/moneyFormat';
import { usePermissionRules } from 'hooks/permissionRules';
import { Surgerie } from 'types/surgerie';
import { asInputComponent } from 'components/masked-input/asInputComponent';

const Layout = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
  gap: theme.spacing(2.5),
  alignItems: 'start',
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
}));

const Fields = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const Summary = styled('aside')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: `1px solid ${palette.border}`,
  borderRadius: theme.shape.borderRadius,
  background: palette.soft,
}));

const SummaryRow = styled('div')({ display: 'flex', justifyContent: 'space-between', gap: 12 });

interface SurgerieValuesTabProps {
  budget: Surgerie;
  // orçamento fechado não muda de valor
  editable: boolean;
  onChangeValue(field: 'value_doctor' | 'value_hospital' | 'value_anesthetist', value: string): void;
  onChangePackage(index: number, value: string): void;
}

const SurgerieValuesTab: React.FC<SurgerieValuesTabProps> = ({ budget, editable, onChangeValue, onChangePackage }) => {
  const { checkPermission } = usePermissionRules();

  function renderInput(value: number, allowed: boolean, onChange: (value: string) => void) {
    return (
      <TextField
        fullWidth
        disabled={!editable || !allowed}
        value={value || 0}
        onChange={e => onChange(e.target.value)}
        helperText={editable && !allowed ? 'Sem permissão para alterar este valor' : undefined}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            inputComponent: asInputComponent(MoneyInput),
          },
        }}
      />
    );
  }

  const margin = marginFromSalePrice(budget.value_budget, budget.value_total);

  return (
    <Layout>
      <Fields>
        <FormField label="Médico">
          {renderInput(budget.value_doctor, checkPermission('surgeries.open.edit.doctor-value'), value =>
            onChangeValue('value_doctor', value),
          )}
        </FormField>
        <FormField label="Hospital">
          {renderInput(budget.value_hospital, checkPermission('surgeries.open.edit.hospital-value'), value =>
            onChangeValue('value_hospital', value),
          )}
        </FormField>
        <FormField label="Anestesista">
          {renderInput(budget.value_anesthetist, checkPermission('surgeries.open.edit.anesthetist-value'), value =>
            onChangeValue('value_anesthetist', value),
          )}
        </FormField>
        {budget.packages.map((item, index) => (
          <FormField key={item.id} label={`Pacote: ${item.package?.name || 'Sem nome'}`}>
            {renderInput(item.package?.sale_price || 0, checkPermission('surgeries.open.edit.packages-value'), value =>
              onChangePackage(index, value),
            )}
          </FormField>
        ))}
      </Fields>

      <Summary>
        <Typography variant="caption" color="textSecondary">
          Preço de venda
        </Typography>
        <Typography variant="h5" fontWeight={800} color="primary">
          {moneyFormat(budget.value_total)}
        </Typography>
        <SummaryRow>
          <Typography variant="body2" color="textSecondary">
            Custo total
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {moneyFormat(budget.value_budget)}
          </Typography>
        </SummaryRow>
        <SummaryRow>
          <Typography variant="body2" color="textSecondary">
            Lucro
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {moneyFormat(budget.value_total - budget.value_budget)}
          </Typography>
        </SummaryRow>
        <SummaryRow>
          <Typography variant="body2" color="textSecondary">
            Margem
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {percentFormat(margin)}
          </Typography>
        </SummaryRow>
      </Summary>
    </Layout>
  );
};

export default SurgerieValuesTab;
